import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { PartnerAccess } from '@/components/Accounts/PartnerAccess';
import { Field, FormSection } from '@/components/Admin/AdminFormFields';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { getPartner } from '@/data/partnersRepo';
import { useAutosavePartner } from '@/hooks/autosave/useAutosavePartner';
import { describeSectionSave } from '@/hooks/autosave/util_historyMessage';
import { useSaveStack } from '@/hooks/autosave/util_saveStack';
import { usePartnerUsers } from '@/hooks/useInvites';
import type { HistoryInfoType } from '@/types/types_history';
import { makeEmptyPartner, type PartnerType } from '@/types/types_partner';
import { partnerRoleLabels } from '@/types/types_partnerUser';

// Fields a partner may edit, grouped by the card they appear in. Each group
// saves independently, and the union of these is exactly the key set the
// security rules allow a partner owner to write — `name` and `brand` are
// Eighty Days' to change, so they are absent here and shown read-only.
const sectionFields = {
  partner: ['website', 'address'],
  contact: ['contactName', 'position', 'contactEmail', 'phone'],
  accounting: ['accountingName', 'accountingEmail', 'billingAddress'],
} as const;

type SectionKeyType = keyof typeof sectionFields;
type EditableFieldType = (typeof sectionFields)[SectionKeyType][number];

// Named in the history step, so an admin reading the log sees which card the
// partner saved.
const sectionLabels: Record<SectionKeyType, string> = {
  partner: 'Partner',
  contact: 'Primary Contact',
  accounting: 'Accounting',
};

// ...and which fields within it. The admin form records one step per field with
// its new value; a card save is one step, so it names the fields instead —
// values are omitted because there can be four of them in one entry.
const fieldLabels: Record<EditableFieldType, string> = {
  website: 'website',
  address: 'partner address',
  contactName: 'contact name',
  position: 'position',
  contactEmail: 'contact email',
  phone: 'phone',
  accountingName: 'accounting name',
  accountingEmail: 'accounting email',
  billingAddress: 'billing address',
};

/**
 * The partner's own view of their record. Unlike the admin partner form, this
 * saves explicitly: each card has its own Save button, so a concierge sees when
 * their change has been stored rather than trusting a field to persist on blur.
 *
 * Only the interaction model differs. The write itself goes through the same
 * autosave path as the admin form, so a partner's edit lands in the document's
 * history exactly like ours does — an admin reading the log sees an unbroken
 * timeline of who changed what. Partners don't see that log: no history panel
 * here, and `partnershistory` is admin-only in the security rules.
 *
 * Staff accounts (`role: 'member'`) get a read-only view of the whole profile.
 */
export function PagePartnerProfile() {
  const { partnerUser } = useAuth();
  const partnerId = partnerUser?.partnerId;
  const canEdit = partnerUser?.role === 'owner';

  // `form` is what's on screen; `saved` is what Firestore last confirmed.
  // Comparing the two per section gives both the dirty state and the reset.
  const [form, setForm] = useState<PartnerType>(() => makeEmptyPartner());
  const [saved, setSaved] = useState<PartnerType>(() => makeEmptyPartner());
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [savingSection, setSavingSection] = useState<SectionKeyType | null>(null);
  const [justSaved, setJustSaved] = useState<SectionKeyType | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Kept in a ref rather than state: nothing renders it here, and each save
  // needs the value left by the previous one without waiting for a re-render.
  const historyRef = useRef<HistoryInfoType | undefined>(undefined);

  const { partnerUsers } = usePartnerUsers(partnerId ?? '');
  // Not rendered on this page — the partner sees per-card Save buttons instead.
  // It still earns its place: the stack warns before leaving with a write in
  // flight.
  const saveStack = useSaveStack();
  const autosave = useAutosavePartner(partnerId, saveStack);

  useEffect(() => {
    if (!partnerId) return;
    let cancelled = false;
    getPartner(partnerId)
      .then((partner) => {
        if (cancelled) return;
        if (partner) {
          setForm(partner);
          setSaved(partner);
          historyRef.current = partner.history;
        } else {
          setNotFound(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [partnerId]);

  function change(field: EditableFieldType, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setJustSaved(null);
  }

  function isDirty(section: SectionKeyType): boolean {
    return sectionFields[section].some((f) => form[f] !== saved[f]);
  }

  async function saveSection(section: SectionKeyType) {
    if (!partnerId) return;
    setSavingSection(section);
    setError(null);
    // Only this card's fields go up, so two people editing different cards
    // don't overwrite each other.
    const changes = Object.fromEntries(sectionFields[section].map((f) => [f, form[f]]));
    // One history step per Save, not per field: the button makes the partner's
    // intent explicit, so the log reads "Updated Accounting" rather than four
    // separate entries eating the retained window.
    const historyBefore = historyRef.current;
    const changedLabels = sectionFields[section].filter((f) => form[f] !== saved[f]).map((f) => fieldLabels[f]);
    const { newHistory, saved: writePromise } = autosave(
      describeSectionSave('Partner', sectionLabels[section], changedLabels),
      changes,
      historyBefore,
    );
    historyRef.current = newHistory;
    try {
      await writePromise;
      setSaved((prev) => ({ ...prev, ...changes }));
      setJustSaved(section);
    } catch (e) {
      console.error('Saving partner profile failed', e);
      setError('Could not save your changes. Please try again.');
      // The step never reached Firestore, so put the counter back — otherwise
      // the next save would skip a step number and leave a hole in the log.
      historyRef.current = historyBefore;
    } finally {
      setSavingSection(null);
    }
  }

  function resetSection(section: SectionKeyType) {
    const original = Object.fromEntries(sectionFields[section].map((f) => [f, saved[f]]));
    setForm((prev) => ({ ...prev, ...original }));
    setJustSaved(null);
  }

  // Save / Discard footer, spanning the full width of its card. A plain render
  // function rather than a component: declaring a component inside render would
  // remount it — and drop its state — on every keystroke.
  function renderSectionActions(section: SectionKeyType) {
    if (!canEdit) return null;
    const dirty = isDirty(section);
    const busy = savingSection === section;
    return (
      <div className='tw:flex tw:items-center tw:justify-end tw:gap-3 tw:sm:col-span-2'>
        {justSaved === section && !dirty && <span className='tw:text-xs tw:text-ed-stone'>Saved</span>}
        {dirty && (
          <Button type='button' variant='ghost' size='sm' disabled={busy} onClick={() => resetSection(section)}>
            Discard
          </Button>
        )}
        <Button type='button' size='sm' disabled={!dirty} loading={busy} onClick={() => void saveSection(section)}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </div>
    );
  }

  if (loading) {
    return <div className='tw:py-16 tw:text-center tw:text-muted-foreground'>Loading…</div>;
  }

  if (notFound || !partnerId) {
    return (
      <div className='tw:py-16 tw:text-center'>
        <p className='tw:text-lg'>We couldn&rsquo;t find your partner record.</p>
        <p className='tw:mt-2 tw:text-sm tw:text-muted-foreground'>Please contact Eighty Days.</p>
      </div>
    );
  }

  return (
    // Supplies its own padding: unlike the admin CMS, this page sits in the
    // public top-nav layout, which leaves spacing to the page.
    <div className='tw:mx-auto tw:max-w-7xl tw:px-6 tw:py-8'>
      <header className='tw:mb-6'>
        <h1 className='tw:font-serif tw:text-3xl tw:font-light'>{form.name}</h1>
        <p className='tw:mt-1 tw:text-sm tw:text-muted-foreground'>
          {canEdit
            ? 'Your organisation’s details with Eighty Days. Save each section after editing.'
            : 'Your organisation’s details with Eighty Days. Only your manager can change these.'}
        </p>
      </header>

      {error && (
        <p className='tw:mb-4 tw:rounded tw:border tw:border-ed-red/30 tw:bg-ed-red/5 tw:px-4 tw:py-2.5 tw:text-sm tw:text-ed-red'>
          {error}
        </p>
      )}

      {/* 2x2 on large screens. `items-start` keeps each card at its natural
          height instead of stretching the shorter one to match its neighbour. */}
      <div className='tw:grid tw:items-start tw:gap-6 tw:xl:grid-cols-2'>
        <FormSection title='Partner'>
          {/* Read-only for everyone here: name and brand identify the partner
              across the CMS and on invitations, so changing them is Eighty
              Days' call. Security rules exclude both from the editable key
              set — the form matches what the server will actually accept. */}
          <Field label='Partner Name'>
            <p className='tw:flex tw:h-9 tw:items-center tw:text-sm'>{form.name}</p>
          </Field>
          <Field label='Brand / Group'>
            <p className='tw:flex tw:h-9 tw:items-center tw:text-sm'>{form.brand || '—'}</p>
          </Field>
          <p className='tw:text-xs tw:text-muted-foreground tw:sm:col-span-2'>
            Contact Eighty Days to change your organisation name or brand.
          </p>
          <Field label='Website'>
            <Input
              value={form.website}
              disabled={!canEdit}
              placeholder='example.com'
              onChange={(e) => change('website', e.target.value)}
            />
          </Field>
          <Field label='Partner Address' className='tw:sm:col-span-2'>
            <Textarea
              rows={2}
              value={form.address}
              disabled={!canEdit}
              placeholder='Full address'
              onChange={(e) => change('address', e.target.value)}
            />
          </Field>
          {renderSectionActions('partner')}
        </FormSection>

        <FormSection title='Primary Contact'>
          <Field label='Contact Name'>
            <Input
              value={form.contactName}
              disabled={!canEdit}
              onChange={(e) => change('contactName', e.target.value)}
            />
          </Field>
          <Field label='Position / Title'>
            <Input
              value={form.position}
              disabled={!canEdit}
              placeholder='e.g., Concierge Manager'
              onChange={(e) => change('position', e.target.value)}
            />
          </Field>
          <Field label='Contact Email'>
            <Input
              type='email'
              value={form.contactEmail}
              disabled={!canEdit}
              placeholder='concierge@example.com'
              onChange={(e) => change('contactEmail', e.target.value)}
            />
          </Field>
          <Field label='Phone Number'>
            <Input
              value={form.phone}
              disabled={!canEdit}
              placeholder='+81-3-XXXX-XXXX'
              onChange={(e) => change('phone', e.target.value)}
            />
          </Field>
          {renderSectionActions('contact')}
        </FormSection>

        <FormSection title='Accounting'>
          <Field label='Accounting Contact Name'>
            <Input
              value={form.accountingName}
              disabled={!canEdit}
              placeholder='Name for invoicing'
              onChange={(e) => change('accountingName', e.target.value)}
            />
          </Field>
          <Field label='Accounting Email'>
            <Input
              type='email'
              value={form.accountingEmail}
              disabled={!canEdit}
              placeholder='accounting@example.com'
              onChange={(e) => change('accountingEmail', e.target.value)}
            />
          </Field>
          <Field label='Billing Address' className='tw:sm:col-span-2'>
            <Textarea
              rows={2}
              value={form.billingAddress}
              disabled={!canEdit}
              placeholder='Full billing address'
              onChange={(e) => change('billingAddress', e.target.value)}
            />
          </Field>
          {renderSectionActions('accounting')}
        </FormSection>

        {/* Team. The manager gets the full card — invite staff, suspend or
            remove them; a staff account gets the roster read-only, since every
            action on it is one `assertCanManagePartnerUser` would refuse. */}
        {canEdit ? (
          <PartnerAccess
            mode='owner'
            partnerId={partnerId}
            partnerName={form.name}
            currentUid={partnerUser?.id}
          />
        ) : (
          <section className='tw:rounded-lg tw:border tw:bg-card tw:p-6'>
            <h2 className='tw:mb-5 tw:text-sm tw:font-semibold tw:tracking-wide tw:text-muted-foreground tw:uppercase'>
              Team
            </h2>
            <ul className='tw:divide-y tw:divide-ed-umber/10'>
              {partnerUsers.map((u) => (
                <li key={u.id} className='tw:flex tw:items-center tw:justify-between tw:gap-3 tw:py-2.5 tw:first:pt-0'>
                  <div className='tw:min-w-0'>
                    <div className='tw:truncate tw:text-sm tw:font-medium'>{u.displayName}</div>
                    <div className='tw:truncate tw:text-xs tw:text-ed-stone'>{u.email}</div>
                  </div>
                  <div className='tw:flex tw:shrink-0 tw:items-center tw:gap-2'>
                    {u.status === 'disabled' && (
                      <span className='tw:rounded-full tw:bg-ed-umber/10 tw:px-2 tw:py-0.5 tw:text-xs tw:text-ed-stone'>
                        Suspended
                      </span>
                    )}
                    <span className='tw:text-xs tw:text-ed-stone'>{partnerRoleLabels[u.role]}</span>
                  </div>
                </li>
              ))}
            </ul>
            <p className='tw:mt-4 tw:text-xs tw:text-muted-foreground'>
              Only your manager can add or remove staff.
            </p>
          </section>
        )}
      </div>
    </div>
  );
}
